/**
 * UiState — per-client UI state that sits alongside GameState.
 *
 * This state is INTENTIONALLY separate from GameState because:
 *   - Selection, camera position, view mode, and hover are local to each player.
 *   - They must never be part of the command stream or the save format
 *     (MULTIPLAYER §2: only sim-affecting mutations travel between peers).
 *   - Mutating them must not advance the RNG or affect tick reproducibility.
 *
 * The renderer and DOM overlays read both GameState and UiState each frame.
 *
 * Views (UI_UX §2, GALAXY §3.5):
 *   - 'galaxy' view: strategic star map. Uses `camera` (galaxy-space 0..1).
 *   - 'system' view: tactical view of a single star system. Uses
 *     `focusedSystemId` to pick which system to draw. v1 has no in-system
 *     pan/zoom — the view auto-fits the system into the viewport.
 *
 * Transitions between the two views cross-fade over ~300ms.
 */

import type {
  EmpireId,
  EntityId,
  GameState,
  PlanetId,
  RaceId,
  ShipInstance,
  ShipInstanceId,
  SystemId,
} from '../state/types/index';
import { EMPIRE_NEUTRAL_ID } from '../state/types/index';

export interface Camera {
  /** World-space coordinates of the point the camera is centered on (0..1 galaxy coords). */
  centerX: number;
  centerY: number;
  /** Zoom multiplier. 1 = whole galaxy fits in the view; >1 zooms in; <1 zooms out. */
  zoom: number;
}

export type ViewMode = 'galaxy' | 'system';

/**
 * v4M6.2 — strategic overlay selector. Mutually exclusive (one at a
 * time, per `docs/MAP.md §3.3`). Toggleable from the HUD overlay row
 * and the P / T / E / D hotkey cluster (re-press the active letter
 * to toggle back to `'none'`). Persisted via `loadGalaxyOverlay` /
 * `saveGalaxyOverlay` in `src/render/galaxy-overlays.ts` so the
 * choice survives a page reload without touching the save format.
 */
export type GalaxyOverlay =
  | 'none'
  | 'production'
  | 'threat'
  | 'exploration'
  | 'diplomatic';

export type SelectionTarget =
  | { kind: 'none' }
  | { kind: 'system'; id: SystemId }
  | { kind: 'planet'; id: PlanetId }
  | { kind: 'ship'; id: ShipInstanceId }
  /**
   * v3M15.1 — Terrain feature (`EnvironmentEntity`): asteroid field, gas
   * giant, nebula, ion cloud, radiation belt, black hole. The detail
   * panel surfaces a per-kind description so the player can read what
   * each terrain type means mechanically (extractor build site / hazard /
   * cosmetic).
   */
  | { kind: 'environment'; id: EntityId };

/**
 * An in-progress cross-fade between views. The renderer computes progress as
 * (now - startMs) / durationMs clamped to [0,1]. When progress reaches 1 the
 * transition is effectively over and can be cleared lazily.
 */
export interface ViewTransition {
  fromView: ViewMode;
  toView: ViewMode;
  startMs: number;
  durationMs: number;
}

/**
 * An in-progress drag-to-select rectangle, in screen-space pixels. Created on
 * left-mousedown in tactical view over empty space and updated on mousemove
 * until mouseup, at which point all ships inside the rect become selected
 * (or added, if shift was held). null when no marquee is active.
 */
export interface Marquee {
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  /** Whether shift was held at drag start (adds to selection instead of replacing). */
  additive: boolean;
}

export interface UiState {
  /** Current view. 'galaxy' is the strategic star map, 'system' is tactical. */
  viewMode: ViewMode;
  /** The system shown in 'system' view; also the auto-focus target when the
   * player zooms past the galaxy→system threshold. null when nothing has been
   * focused yet.  */
  focusedSystemId: SystemId | null;
  /** Active cross-fade, if any. */
  viewTransition: ViewTransition | null;
  /** Galaxy-view camera (galaxy-space 0..1). Independent of systemCamera. */
  galaxyCamera: Camera;
  /** In-system camera (local 0..1 coords, GALAXY §3.5.1). Independent of galaxyCamera. */
  systemCamera: Camera;
  selection: SelectionTarget;
  /**
   * The set of ships currently selected in tactical view. Separate from
   * `selection` so we can highlight many ships while still showing a single
   * target's info in the detail panel — when exactly one ship is selected,
   * `selection` mirrors it for panel compatibility.
   */
  selectedShipIds: Set<ShipInstanceId>;
  /** Active drag-to-select rectangle, or null if no marquee is in progress. */
  marquee: Marquee | null;
  /**
   * v4M5.3b3e — system the cursor is hovering in galaxy view, or null.
   * Drives the jump-preview tooltip showing destination name + fuel
   * cost for the currently-selected fleet. Purely a cosmetic hover
   * signal — distinct from `selection` which persists across mouse
   * moves.
   */
  hoveredGalaxySystemId: SystemId | null;
  /**
   * Screen-space cursor position when `hoveredGalaxySystemId` is
   * non-null; the DOM tooltip uses this to anchor.
   */
  hoveredGalaxyAnchor: { x: number; y: number } | null;
  /**
   * Whether the player is currently mid-drag (mouse button held + moved).
   * Used so click handlers can distinguish click-to-select from drag-to-pan.
   */
  isDragging: boolean;
  /**
   * Dev-only detection overlay toggle (M8-F). When true, the system renderer
   * draws every ship's vision + scan rings (all teams, not just the player's)
   * and pastes effective-detection / effective-stealth labels under each
   * chevron. Toggled via F9. Off by default — intended for balance iteration
   * and internal QA, not shipped-game UX.
   */
  devDetectionOverlay: boolean;
  /**
   * Dev-only enemy-POV overlay toggle (M8.1-F). When true, renderers swap
   * their "viewer empire" from the player's empire to the first non-player
   * empire — so fog of war, ship-visibility filtering, and detection
   * overlays all reflect what *that* empire sees. Lets us verify that a
   * cloaked player ship really does vanish from the enemy's scanners
   * without hacking a second client. Toggled via F10. Off by default.
   */
  enemyPovOverlay: boolean;
  /**
   * Dev-only reveal-all toggle (v3M13). When true, all FoW gates in the
   * renderers short-circuit: every ship renders at its live position
   * regardless of teamVision; the galaxy-view enemy-stack pass anchors
   * to the live `ship.systemId` rather than the last-seen system; the
   * VFX layer renders weapons-fire from any empire. Strictly local UI
   * state — no command-stream side effects, same MULTIPLAYER §2
   * reasoning as F9/F10. Toggled via the dev-toolbox panel (no direct
   * hotkey). Off by default.
   */
  devRevealAll: boolean;
  /**
   * Whether the dev-toolbox panel (v3M13) is open. Backtick toggles. The
   * panel surfaces F9 / F10 / reveal-all checkboxes plus per-resource
   * +10k grant buttons. Strictly local UI state.
   */
  devToolboxOpen: boolean;
  /**
   * Active M14 placement mode, or null. Set when the player clicks a
   * Build Space Structure button in the Constructor's detail panel; the
   * input handler intercepts the next tactical-view click to dispatch
   * `buildSpaceStructure` with the cursor's world coords, and the
   * system-view renderer draws a ghost at the cursor (green if the
   * target location is a valid site for the kind, red if not). Escape
   * or right-click cancels.
   */
  buildPlacement: { constructorShipId: ShipInstanceId; structureKind: string } | null;
  /**
   * Cursor position in tactical-view world coords (local 0..1), or null
   * if the mouse isn't over the tactical view or we're in galaxy view.
   * Updated on mousemove; used by the M14 placement-mode ghost so the
   * renderer knows where to draw without re-deriving from screen coords.
   */
  cursorWorld: { sx: number; sy: number; systemId: SystemId } | null;
  /**
   * v3M16 — Player-defined fleet groupings (classic RTS control groups).
   * Keyed by fleet number 0..9. Each value is the set of ship ids
   * stored in that slot. Empty slots are absent from the map (Map.get
   * returns undefined). Members can become stale when ships despawn —
   * `recallFleet` and `getFleetRoster` filter live ships at read time
   * rather than coupling to the ship-despawn paths.
   *
   * Strictly UI state per the file-header convention: never serialised,
   * never on the command stream. Fleets reset on session restart;
   * persistence is a deferred polish item.
   */
  fleets: Map<number, Set<ShipInstanceId>>;
  /**
   * v3M18 — active "Connect Warp Gate" target-picker. Set when the
   * player clicks the Connect button in a system's detail panel; the
   * galaxy-view renderer paints target rings on every same-empire
   * operational unconnected gate-system, and a galaxy-view LMB
   * intercept dispatches `connectWarpGate` with the picked target.
   * Esc cancels (clears this field). Right-click also cancels for
   * symmetry with the M14 placement-mode UX. Strictly local UI state
   * — never serialised, never on the command stream.
   */
  connectWarpGateMode: {
    fromGateId: ShipInstanceId;
    fromSystemId: SystemId;
  } | null;
  /**
   * v3M22.4 — active "Guard target" picker. Set when the player clicks
   * the Guard button on a friendly ship's detail panel; the system-view
   * renderer paints target rings on every same-system planet (any
   * owner) and friendly ship in the picker ship's system, and a system-
   * view LMB intercept dispatches `setShipGuard` with the picked target.
   * Esc / RMB cancel (clears this field). Strictly local UI state —
   * never serialised, never on the command stream. Mirrors v3M18's
   * `connectWarpGateMode`.
   */
  guardTargetMode: {
    shipId: ShipInstanceId;
    systemId: SystemId;
  } | null;
  /**
   * MFix11.3 — active Dev Toolbox spawn-entity placement mode. Set when
   * the player toggles "Activate Spawning Mode" in the Dev Toolbox with
   * empire / race / chassis selections. While active, the system-view
   * cursor becomes a crosshair and left-clicks spawn the configured
   * entity at the click position via `devSpawnEntity`. Multi-click —
   * the mode stays active after each spawn until toggled off or
   * Escape pressed. Mutually exclusive with `buildPlacement` /
   * `connectWarpGateMode` / `guardTargetMode` (the enter helper
   * clears them). Strictly local UI state — never serialised, never on
   * the command stream.
   */
  devSpawnMode: {
    ownerEmpireId: EmpireId;
    raceId: RaceId;
    chassis: string;
  } | null;
  /**
   * v4M6.2 — currently active strategic overlay (galaxy view only).
   * Mutually exclusive per `docs/MAP.md §3.3`. Default `'none'`.
   * Persisted to localStorage on change via `saveGalaxyOverlay` and
   * loaded at session boot via `loadGalaxyOverlay` (both in
   * `src/render/galaxy-overlays.ts`). Strictly local UI state — never
   * serialised into the save body, never on the command stream.
   */
  galaxyOverlay: GalaxyOverlay;
  /**
   * v4M6.6 — galaxy-view in-flight ship under the cursor, or null. Set
   * by the input handler's mousemove gate when a system-hover miss is
   * within hit-radius of an in-flight ship dot (own / teammate /
   * visible-enemy classifications per v4M6.3b's `classifyShipForViewer`).
   * Read by `fleet-composition-tooltip.ts` to surface chassis breakdown
   * (own + teammate full detail, visible enemy degraded to "Enemy
   * fleet (N ships)" per `MAP.md §4.3.2` intel asymmetry). Strictly
   * local UI state — never serialised, never on the command stream.
   */
  hoveredInFlightShipId: ShipInstanceId | null;
}

export function createUiState(): UiState {
  return {
    viewMode: 'galaxy',
    focusedSystemId: null,
    viewTransition: null,
    galaxyCamera: { centerX: 0.5, centerY: 0.5, zoom: 1 },
    systemCamera: defaultSystemCamera(),
    selection: { kind: 'none' },
    selectedShipIds: new Set(),
    marquee: null,
    hoveredGalaxySystemId: null,
    hoveredGalaxyAnchor: null,
    isDragging: false,
    devDetectionOverlay: false,
    enemyPovOverlay: false,
    devRevealAll: false,
    devToolboxOpen: false,
    buildPlacement: null,
    cursorWorld: null,
    fleets: new Map(),
    connectWarpGateMode: null,
    guardTargetMode: null,
    devSpawnMode: null,
    // v4M6.2 — `'none'` is the default; callers that want the user's
    // last-saved overlay restore it via `loadGalaxyOverlay()` and
    // assign here on session boot.
    galaxyOverlay: 'none',
    // v4M6.6 — populated lazily by input.ts mousemove.
    hoveredInFlightShipId: null,
  };
}

/**
 * MFix11.3 — enter Dev Toolbox spawn-entity placement mode. Caller is
 * responsible for resolving the empire / race / chassis trio at the
 * UI level; this helper does no validation. Mutually exclusive with
 * the other placement modes — clears them on enter.
 */
export function enterDevSpawnMode(
  ui: UiState,
  ownerEmpireId: EmpireId,
  raceId: RaceId,
  chassis: string,
): void {
  ui.devSpawnMode = { ownerEmpireId, raceId, chassis };
  ui.buildPlacement = null;
  ui.connectWarpGateMode = null;
  ui.guardTargetMode = null;
}

/** MFix11.3 — exit Dev Toolbox spawn-entity mode (toggle off, Esc, or RMB). */
export function exitDevSpawnMode(ui: UiState): void {
  ui.devSpawnMode = null;
}

/**
 * v3M18 — enter the Connect Warp Gate target picker. Caller is responsible
 * for resolving `fromGateId` to a same-empire operational unconnected
 * Warp Gate; this helper does no validation. UI clears any other mutually
 * exclusive mode (currently only `buildPlacement`) so two pickers can't
 * be active at once.
 */
export function enterConnectWarpGateMode(
  ui: UiState,
  fromGateId: ShipInstanceId,
  fromSystemId: SystemId,
): void {
  ui.connectWarpGateMode = { fromGateId, fromSystemId };
  ui.buildPlacement = null;
}

/** v3M18 — exit Connect Warp Gate mode (Esc, RMB, or post-commit). */
export function exitConnectWarpGateMode(ui: UiState): void {
  ui.connectWarpGateMode = null;
}

/**
 * v3M22.4 — enter the Guard-target picker. Caller resolves `shipId` to
 * a player-owned ship; this helper does no validation. Other mutually-
 * exclusive UI modes (`buildPlacement`, `connectWarpGateMode`) are
 * cleared so two pickers can't be active at once.
 */
export function enterGuardTargetMode(
  ui: UiState,
  shipId: ShipInstanceId,
  systemId: SystemId,
): void {
  ui.guardTargetMode = { shipId, systemId };
  ui.buildPlacement = null;
  ui.connectWarpGateMode = null;
}

/** v3M22.4 — exit Guard-target mode (Esc, RMB, or post-commit). */
export function exitGuardTargetMode(ui: UiState): void {
  ui.guardTargetMode = null;
}

/**
 * v3M16 — Valid fleet slot ids. Mirrors the Ctrl+N / N keyboard
 * affordance: keys 0-9 map directly to fleet ids 0-9.
 */
export const FLEET_IDS: readonly number[] = Object.freeze([0, 1, 2, 3, 4, 5, 6, 7, 8, 9]);

/**
 * v3M16 — Snapshot the current `selectedShipIds` into the named fleet
 * slot. No-op when the selection is empty (silent — we don't clear the
 * slot or beep at the player). Replaces any existing slot contents.
 *
 * Stores ship ids only; stale entries (ships destroyed after assignment)
 * are filtered at read time by `recallFleet` / `getFleetRoster` rather
 * than scrubbed eagerly.
 */
export function assignFleet(ui: UiState, fleetId: number): void {
  if (ui.selectedShipIds.size === 0) return;
  ui.fleets.set(fleetId, new Set(ui.selectedShipIds));
}

/**
 * v3M16 — Recall a fleet into the active selection. Replaces
 * `selectedShipIds` with the slot's contents filtered against the live
 * ship registry — destroyed ships drop out automatically. Updates
 * `selection` to mirror the multi-select bridge: single live ship ⇒
 * `selection: { kind: 'ship', id }`; otherwise (empty or many) ⇒
 * `selection: { kind: 'none' }` so the right-hand panel doesn't show
 * stale info. No-op when the slot is empty after filtering.
 */
export function recallFleet(ui: UiState, state: GameState, fleetId: number): void {
  const slot = ui.fleets.get(fleetId);
  if (!slot || slot.size === 0) return;
  const live = new Set<ShipInstanceId>();
  for (const id of slot) {
    if (state.ships.has(id)) live.add(id);
  }
  if (live.size === 0) return;
  ui.selectedShipIds = live;
  if (live.size === 1) {
    const onlyId = live.values().next().value as ShipInstanceId;
    ui.selection = { kind: 'ship', id: onlyId };
  } else {
    ui.selection = { kind: 'none' };
  }
}

/**
 * v3M16 — Read-side roster for the fleet panel. Returns the live ships
 * in the named slot, in insertion order (Set preserves insertion order
 * deterministically). Stale ids are filtered. Returns an empty array
 * for unset slots and slots whose members have all been destroyed.
 */
export function getFleetRoster(
  ui: UiState,
  state: GameState,
  fleetId: number,
): ShipInstance[] {
  const slot = ui.fleets.get(fleetId);
  if (!slot || slot.size === 0) return [];
  const out: ShipInstance[] = [];
  for (const id of slot) {
    const ship = state.ships.get(id);
    if (ship) out.push(ship);
  }
  return out;
}

/**
 * v3M16 — Center the camera on a fleet (classic RTS double-tap-N
 * gesture). Picks the system holding the most fleet members and
 * focuses there; centers the system camera on the centroid of fleet
 * members in that system; switches viewMode to 'system' via a normal
 * cross-fade. Mid-jump members (systemId === null) are excluded — they
 * have no on-screen position.
 *
 * No-op when the fleet has no live members or every live member is
 * mid-jump. Selection is unaffected; the caller is expected to have
 * recalled the fleet on the first tap of the gesture, with this jump
 * happening on the second tap.
 */
export function centerCameraOnFleet(
  ui: UiState,
  state: GameState,
  fleetId: number,
  nowMs: number,
): void {
  const roster = getFleetRoster(ui, state, fleetId);
  if (roster.length === 0) return;

  // Group fleet members by systemId. Skip mid-jump (null) ships — they
  // have no in-system position to centre on.
  const bySystem = new Map<SystemId, ShipInstance[]>();
  for (const ship of roster) {
    if (ship.systemId === null) continue;
    let bucket = bySystem.get(ship.systemId);
    if (!bucket) {
      bucket = [];
      bySystem.set(ship.systemId, bucket);
    }
    bucket.push(ship);
  }
  if (bySystem.size === 0) return; // every member mid-jump

  // Pick the system with the most fleet members; first-seen wins ties.
  let bestSystemId: SystemId | null = null;
  let bestCount = 0;
  for (const [sysId, ships] of bySystem) {
    if (ships.length > bestCount) {
      bestCount = ships.length;
      bestSystemId = sysId;
    }
  }
  if (bestSystemId === null) return;
  const focusShips = bySystem.get(bestSystemId)!;

  // Centroid of the fleet members in that system (in-system local
  // 0..1 coords).
  let sx = 0;
  let sy = 0;
  for (const ship of focusShips) {
    sx += ship.sx;
    sy += ship.sy;
  }
  sx /= focusShips.length;
  sy /= focusShips.length;

  ui.focusedSystemId = bestSystemId;
  ui.systemCamera = { ...ui.systemCamera, centerX: sx, centerY: sy };
  startViewTransition(ui, 'system', nowMs);
}

/**
 * Which empire's POV the renderer should paint this frame. Under normal play
 * it's the player's empire; when the F10 enemy-POV debug overlay is on, we
 * pick a non-player empire.
 *
 * Picking heuristic (M8.1-F rev.2): prefer the non-player empire that has a
 * ship in the currently focused system — that's the empire whose detection
 * rings matter for the scene on screen. If no non-player empire has presence
 * in the focused system (or we're in galaxy view with nothing focused), fall
 * back to the first non-player empire in ascending-id order so the choice
 * stays stable across frames and save loads. Without this preference the
 * overlay is a silent no-op any time the player moves ships to a system that
 * happens to belong to the "second" AI — the picker would still return the
 * first AI and show an empty vision map.
 */
export function viewerEmpireId(state: GameState, ui: UiState): EmpireId {
  if (!ui.enemyPovOverlay) return state.playerEmpireId;
  const ids = [...state.empires.keys()].sort((a, b) => a - b);
  // v3M21.1 — synthetic neutral empire has an empty teamVision map (no
  // ships, no scanners) so picking it for the enemy-POV overlay would
  // show an empty viewport. Exclude alongside the player.
  const nonPlayerIds = ids.filter(
    (id) => id !== state.playerEmpireId && id !== EMPIRE_NEUTRAL_ID,
  );
  if (nonPlayerIds.length === 0) return state.playerEmpireId;

  // Prefer an enemy with a physical ship in the focused system. Mid-jump ships
  // (systemId === null) don't count — they can't see or be seen in-system.
  if (ui.focusedSystemId !== null) {
    for (const id of nonPlayerIds) {
      for (const ship of state.ships.values()) {
        if (ship.ownerEmpireId === id && ship.systemId === ui.focusedSystemId) {
          return id;
        }
      }
    }
  }

  // Fallback — first non-player by id order.
  return nonPlayerIds[0]!;
}

/**
 * v4M10.3 — `canEnterSystemView` retired per `FOW.md §3.4`. Drill-in is
 * now unconditional; the renderer's Unexplored branch in
 * `src/render/system-view.ts` (driven by `currentLayerForSystem` in
 * `src/state/fow.ts`) handles the visual for unscouted systems. Layer
 * resolution unified under the FoW resolver so a single source of
 * truth — Unexplored / Fog of War / Seen — drives every consumer.
 */

/**
 * The canonical default system-view camera. The tactical view opens at
 * `SYSTEM_VIEW_DEFAULT_ZOOM` so the system fills the viewport with room to
 * spare — the player gets a real tactical map, not a shrunk galaxy. Callers
 * that re-enter the system view (wheel threshold, double-click, mini-galaxy
 * click) should reset via this helper so the framing stays consistent.
 */
export function defaultSystemCamera(): Camera {
  return { centerX: 0.5, centerY: 0.5, zoom: SYSTEM_VIEW_DEFAULT_ZOOM };
}

/**
 * Pick the active camera for the current view mode. Helpers + input handlers
 * can use this so they don't have to branch on viewMode.
 */
export function activeCamera(ui: UiState): Camera {
  return ui.viewMode === 'galaxy' ? ui.galaxyCamera : ui.systemCamera;
}

/** Mutate the active camera for the current view mode. */
export function setActiveCamera(ui: UiState, camera: Camera): void {
  if (ui.viewMode === 'galaxy') ui.galaxyCamera = camera;
  else ui.systemCamera = camera;
}

/** Zoom bounds — below MIN the galaxy shrinks to unreadable; above MAX systems sprawl. */
export const MIN_ZOOM = 0.5;
export const MAX_ZOOM = 8;

/**
 * When zoomed in past this level in galaxy view, we auto-transition to the
 * system view for the nearest system to the viewport centre. At the default
 * per-notch wheel factor (*1.1) this takes ~13 notches from zoom 1 — tuned
 * for snappy drill-in without feeling jumpy.
 */
export const GALAXY_TO_SYSTEM_ZOOM = 3.5;

/**
 * Galaxy-camera zoom value we snap back to when returning from system view.
 * Kept comfortably below GALAXY_TO_SYSTEM_ZOOM so the next wheel-in doesn't
 * immediately trip the galaxy->system transition again (input.ts returns at
 * SYSTEM_TO_GALAXY_ZOOM - 0.5, giving ~5–6 wheel notches of buffer before
 * re-entry).
 */
export const SYSTEM_TO_GALAXY_ZOOM = 2.5;

/**
 * Default in-system camera zoom used whenever the system view is entered or
 * re-framed. At zoom 1 the whole [0,1] box fits the letterboxed square
 * sub-viewport so the player sees the whole system on drill-in; zooming in
 * reveals tactical detail (planets/jump points spread apart around the fixed
 * star sprite — see GALAXY §3.5.1).
 */
export const SYSTEM_VIEW_DEFAULT_ZOOM = 1;

/**
 * In-system camera zoom floor — zooming out past this in system view trips a
 * cross-fade back to the galaxy view. Kept comfortably above MIN_ZOOM so the
 * handoff fires before the camera clamp would trap the player.
 */
export const SYSTEM_VIEW_EXIT_ZOOM = 0.7;

/** Default cross-fade duration in milliseconds. */
export const VIEW_TRANSITION_MS = 300;

export function clampZoom(z: number): number {
  return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z));
}

/** Progress of a transition in [0, 1]. Returns 1 when t is null or already complete. */
export function transitionProgress(t: ViewTransition | null, nowMs: number): number {
  if (!t) return 1;
  if (t.durationMs <= 0) return 1;
  const p = (nowMs - t.startMs) / t.durationMs;
  if (p <= 0) return 0;
  if (p >= 1) return 1;
  return p;
}

/**
 * Start (or replace) a cross-fade to `to`. Sets `ui.viewMode` to the target
 * view immediately; the renderer uses `ui.viewTransition` to blend the two.
 * If we're already in the target view and no transition is in flight, this
 * is a no-op so the caller doesn't have to guard.
 */
export function startViewTransition(
  ui: UiState,
  to: ViewMode,
  nowMs: number,
  durationMs: number = VIEW_TRANSITION_MS,
): void {
  if (ui.viewMode === to && ui.viewTransition === null) return;
  const from = ui.viewMode;
  ui.viewMode = to;
  ui.viewTransition =
    from === to
      ? null
      : { fromView: from, toView: to, startMs: nowMs, durationMs };
}

/**
 * Pick the system whose galaxy position is closest to the current camera
 * centre. Used when the player zooms past the galaxy->system threshold and we
 * need to choose a focus target.
 */
export function nearestSystemToCamera(
  systems: Iterable<{ id: SystemId; x: number; y: number }>,
  camera: Camera,
): SystemId | null {
  let best: { id: SystemId; dist: number } | null = null;
  for (const s of systems) {
    const d = Math.hypot(s.x - camera.centerX, s.y - camera.centerY);
    if (!best || d < best.dist) best = { id: s.id, dist: d };
  }
  return best?.id ?? null;
}

/**
 * Normalise a screen-space marquee into its ordered corner pair (minX, minY,
 * maxX, maxY) so callers can do a straight bounds-test without worrying which
 * direction the drag went.
 */
export function marqueeBounds(m: Marquee): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} {
  return {
    minX: Math.min(m.startX, m.currentX),
    minY: Math.min(m.startY, m.currentY),
    maxX: Math.max(m.startX, m.currentX),
    maxY: Math.max(m.startY, m.currentY),
  };
}
